import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { usePatientsStore } from "@/stores/patientsStore";
import { useToast } from "@/components/ui/use-toast";

interface AddTagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientIds: string[];
}

const AddTagsModal = ({ isOpen, onClose, patientIds }: AddTagsModalProps) => {
  const [tagInput, setTagInput] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addTagsToMultiplePatients } = usePatientsStore();
  const { toast } = useToast();

  const handleAddTag = () => {
    if (!tagInput.trim()) return;

    // Prevent duplicates
    if (!selectedTags.includes(tagInput.trim())) {
      setSelectedTags([...selectedTags, tagInput.trim()]);
    }
    setTagInput("");
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async () => {
    if (selectedTags.length === 0) {
      toast({
        title: "No tags selected",
        description: "Please add at least one tag to continue.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await addTagsToMultiplePatients(patientIds, selectedTags);
      toast({
        title: "Tags added successfully",
        description: `Added ${selectedTags.length} tag(s) to ${patientIds.length} patient(s).`,
      });
      onClose();
      setSelectedTags([]);
    } catch (error) {
      toast({
        title: "Failed to add tags",
        description: "An error occurred while adding tags to patients.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Tags to Selected Patients</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Enter tag name..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Button
              type="button"
              size="sm"
              onClick={handleAddTag}
              disabled={!tagInput.trim()}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedTags.map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="flex items-center gap-1 py-1 px-2"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="hover:text-destructive focus:outline-none"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="text-sm text-muted-foreground">
            {patientIds.length} patient{patientIds.length !== 1 ? "s" : ""}{" "}
            selected
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedTags.length === 0 || isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Save Tags"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTagsModal;
